// The functional Colony play view (task 8.4; UI.md §4; DESIGN §9; design D2-D12).
// Composes the shared store (selectors) + the injected `ColonyController` into
// the layered surface the LOCKED structural spec requires: game canvas (out of
// this change's scope — the world renderer is `client-scaffold`'s; this screen
// composes the HUD/dock/sheet chrome that sits over it) -> top HUD -> bottom
// bee-type dock -> scrim + bee-type sheet (header/buff/birth-type, version bar,
// flowchart/code tabs, prompt box) -> the create-type flow. Every mutation
// (`Prompt`/`Rollback`/`CreateType`/`SetBirthType`) flows through the injected
// `controller.dispatch` seam (design D3/D7/D8) — this component never
// constructs a `Connection` of its own; production wiring (the shared
// session-scoped connection + a real `ColonyController`) is task group 9.
//
// Deliberate scope trim vs the `colony.html` sketch (documented, not silent):
// the create-type flow omits the "start from a preset / blank / copy a type"
// segmented control — no bound scenario exercises it, and the wire's
// `InitialProgram` only needs *a* value, so this always creates from a blank
// preset. Nothing about the wire contract or the extension-colour/cap/dispatch
// behaviour is affected.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  selectOwnEliminated,
  selectOwnScoreboardRow,
  useClientStore,
  versionCacheKey,
} from '../../state/store';
import { InMatchCanvas, type InMatchRenderer } from '../../app/InMatchCanvas';
import { COLONY_CLOSE_SCALE, COLONY_NOMINAL_VIEWPORT, screenToWorld } from '../../canvas/projection';
import { pickEntityAt } from '../../canvas/pick';
import type { WorldSnapshot } from '../../net/wire/connection';
import type { MatchSessionKey } from '../../session/match-session';
import { Tier, type ViewportRect } from '../../net/wire/messages';
import { type ColonyController, type ScriptingMutation } from '../colony/controller';
import { deriveVersionBarModel, formatProvenanceSummary } from '../colony/version-model';
import type { VersionBarModel } from '../colony/version-model';
import { BuffName } from '../../net/wire/messages';
import type { PromptStatus, RejectReason, StateHistogram, TypeMeta } from '../../net/wire/messages';
import { SeasonClock } from '../components/hud/SeasonClock';
import { ResourceCounter } from '../components/hud/ResourceCounter';
import { HUD_ICONS } from '../components/hud/hud-icons';
import { BeeTypeButton } from '../components/hud/BeeTypeBar';
// The one preset sprite/colour table, shared with the sandbox screen's chips (see the note beside
// `countForType` below for why this screen no longer keeps its own copy).
import { colorForType, extensionPaletteIndex, spriteForType } from '../rounds/type-visuals';
import { CameraControl } from '../components/hud/CameraControl';
import { BeeInspectPanel } from '../components/hud/BeeInspectPanel';
import { beeInspectModel } from '../colony/bee-inspect-model';
import type { BeeInspectModel, InspectTypeRef } from '../colony/bee-inspect-model';
import { SoundControl } from '../components/hud/SoundControl';
import type { SoundControlSeams } from '../components/hud/SoundControl';
import {
  cameraForZoomCentre,
  defaultTierForZoom,
  panByScreenDelta,
  zoomAboutScreenPoint,
  type CameraGestureBounds,
} from '../camera/camera-gestures';
import { useCanvasCameraGestures, type CanvasCameraGestureHandlers } from '../camera/use-canvas-camera-gestures';
import { Sheet } from '../components/core/Sheet';
// --- the pvp prototype's rounds-shaped prompt surface (`docs/dev/pvp-prototype.md`) ---------
// Reached only through the optional `pvpChat` prop below; with it absent none of this renders and
// the tree is exactly what live mode has always drawn.
import { PromptBar } from '../components/hud/PromptBar';
import { ChatOverlay } from '../components/chat/ChatOverlay';
import { TypeSheet } from '../components/script/TypeSheet';
import { useChatSeam, isChatBusy, type ChatPhase, type ChatSeam } from '../colony/chat-seam';
import { summarizeThread } from '../chat/transcript-model';
import { populationFromHistogram } from '../colony/histogram-population';
import { clockLabel } from '../rounds/review-model';
import {
  PROMPT_BAR_HEIGHT,
  PROMPT_BAR_HEIGHT_FOCUSED,
  PROMPT_BAR_THREAD_ROW_HEIGHT,
} from '../rounds/sandbox-layout';
import { Chip } from '../components/core/Chip';
import { Button } from '../components/core/Button';
import { BppProgramDiagram } from '../components/script/BppProgramDiagram';
import { CodeView } from '../components/script/CodeView';
import { PromptBox } from '../components/script/PromptBox';
import type { PromptBoxStatus } from '../components/script/PromptBox';
import { VersionRow } from '../components/script/VersionRow';
import {
  STARTER_PROMPTS,
  STARTER_PROMPT_HINT,
  STARTER_PROMPT_MICROCOPY,
} from '../colony/starter-prompts';
import { FirstRunGuide } from '../components/onboarding/FirstRunGuide';
import { createOnboardingSeenStore } from '../../app/onboarding-store';
import type { OnboardingSeenStore } from '../../app/onboarding-store';
import type { SessionPhase } from '../../app/SessionController';
import { MATCH_DURATION_MIN } from '../../generated/sim-constants';

/** The Colony route's LOD seam (design DD6, `colony-field-canvas`; colony-match-nav): the
 * union of `Match`'s `requestTier`-only `MatchLodSeam` and its `setZoom`/`setViewport`
 * `MatchCameraLodSeam` — the shared session `LodController` already implements this shape
 * structurally, so no adapter object is needed (mirrors how `cameraLod` is wired). Colony
 * reasserts `Close` once on route entry via `requestTier`, then forwards the controlled
 * camera zoom via `setZoom` as the player pans/zooms. The production mapping remains Close
 * at every zoom so symbol mode, rather than an aggregate data tier, owns overview rendering;
 * zoom drives only presentation and this subscription's viewport, never
 * navigation (that is an explicit HUD control, `client-colony`/`client-match`). */
export interface ColonyLodSeam {
  requestTier(tier: Tier): void;
  setZoom(zoom: number): void;
  setViewport(worldRect: ViewportRect): void;
}

/** The Colony route's live close-tier field-underlay seam bundle (design DD5/DD8),
 * derived by `SessionController` from the one session: the session key + renderer factory
 * `InMatchCanvas` consumes, the own hive world anchor (from the store's `ownHive`), and the
 * viewport-carrying Colony LOD seam. The hive `Camera` and the Close viewport are derived
 * inside the underlay from the **measured** container (design DD9 / implementation-review F2),
 * so the raw `ownHive` — not a precomputed camera against a nominal size — is what the seam
 * carries. Optional with an inert default so a bare mount renders the chrome over a
 * placeholder — never a crash. */
export interface ColonyUnderlaySeams {
  sessionKey: MatchSessionKey;
  /**
   * `tickRateHz` is **wire-required** (client-tick-rate-provenance, tick-rate-single-source
   * E4), sourced from `Welcome.manifest.tickRateHz` by `InMatchCanvas`, the shared host this
   * seam's factory is threaded into.
   */
  createRenderer: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  ownHive: [number, number];
  lod: ColonyLodSeam;
}

export interface ColonyProps {
  /** Injectable mutation-dispatch seam (design D3) — production supplies the
   * real controller wrapping the shared session connection (task group 9's
   * `App.tsx` wiring); tests inject `createFakeColonyController()`. Optional
   * so the still-stub `App.tsx` route (unwired until task group 9) keeps
   * compiling; the inert default below never sends anything (always
   * `not-ready`), matching the pre-wiring "not connected yet" reality. */
  controller?: ColonyController;
  /** Injectable close-tier field-underlay seams (design DD5, `colony-field-canvas`).
   * Undefined before a live session exists, so the Colony screen renders chrome-only over a
   * background placeholder (no crash), exactly as `Match`/`InMatchCanvas` do. */
  underlay?: ColonyUnderlaySeams;
  /** The in-match audio-layer seam bundle (client-audio DD6, tasks.md 6.5): the shared
   * lifecycle + preference adapter the composition-root `initAudio` threads in (task group 7).
   * Optional and additive — undefined leaves the HUD exactly as before (no control mounts),
   * mirroring the `controller`/`underlay` inert-seam precedent; no screen-contract change. */
  sound?: SoundControlSeams;
  /** The composition root's session phase (design D-Gate, onboarding): the first-run
   * guidance overlay shows only while `sessionPhase === 'in-match'`. `SessionController`
   * threads it through `AppRoutes`; left `undefined` in a bare mount so the guide never
   * shows outside a live match (and so existing Colony tests are unaffected). */
  sessionPhase?: SessionPhase;
  /** Injectable once-per-browser onboarding "seen" adapter (design D-Persist). Defaults
   * to the `window.localStorage`-backed adapter; tests inject an in-memory double to
   * assert persistence (or its absence) without touching real storage. */
  onboardingSeenStore?: OnboardingSeenStore;
  /**
   * Injectable screen-navigation seam (colony-match-nav, UI.md §3/§4).
   *
   * **This screen no longer calls it at all.** It drove the top-HUD "View match" button, which
   * `player-chat-and-outcome` **D-4** removed; zooming has not navigated since colony-match-nav.
   * The prop is retained deliberately rather than deleted, because what `client-colony` still
   * asserts through it is a *negative*: a pan or a zoom crossing the LOD threshold must navigate
   * nowhere. A spy that is never called is the evidence for that, and the component not
   * destructuring this at all is a stronger guarantee than one that holds a seam it chooses not to
   * use. The shell keeps passing it; nothing here reads it.
   */
  onNavigate?: (path: string) => void;
  /** Injectable zoom->tier mapping (camera-pan-zoom), mirroring `Match`'s own seam — read
   * only for the `CameraControl` chip's display-only tier readout. Defaults to the shared
   * `defaultTierForZoom` (PROTOCOL §4 ±10% hysteresis). */
  tierForZoom?: (zoom: number, current: Tier) => Tier;
  /**
   * **The pvp prototype's Beekeeper seam** (`docs/dev/pvp-prototype.md`). Optional with an inert
   * default, exactly like `controller`/`underlay`/`sound`: **its presence IS the fork.**
   *
   * Absent — every live deployment that does not advertise pvp, and every bare mount — this screen
   * renders precisely the tree it always has: the bee-type dock at the bottom, the scrim + hand-rolled
   * `Sheet`, and `PromptBox` dispatching a one-shot generation over the WebSocket. `Colony.chat.test.tsx`
   * guards that.
   *
   * Present, the screen adopts rounds mode's Sandbox composition, because the pvp Beekeeper *is* the
   * rounds multi-turn agent over the whole colony document: the prompt becomes a globally pinned
   * `PromptBar` at the bottom of the frame, the bee-type panel becomes the inset-popup `TypeSheet`,
   * and Send opens the streaming `ChatOverlay` rather than submitting a one-shot generation. The
   * per-type version/rollback affordance is kept — it rides the sheet's `toolbar` slot.
   *
   * The composition root builds it (`SessionController`) from `net/live-chat.ts`'s transport and the
   * shared `createChatChannel`; this screen constructs no transport of its own, matching every other
   * seam here.
   */
  pvpChat?: ChatSeam;
}

/** The visible size the underlay derives its hive camera + Close viewport from (design DD9,
 * revised per implementation-review F2): the **measured** container, so the hive centres in the
 * real window on desktop/tablet/phone alike. The nominal phone-portrait size is ONLY the
 * zero/unmeasured fallback (jsdom, or a pre-layout first paint), never the unconditional value.
 *
 * The width/height are **integer-rounded** (colony-underlay-canvas-stability): `getBoundingClientRect`
 * returns fractional CSS pixels, and the raw fraction fed straight into the `InMatchCanvas` `key`
 * below let sub-pixel jitter change the key and remount the canvas — one leaked WebGL context per
 * cycle. Rounding alone still flips between N and N+1 when the true size hovers on a half-pixel
 * boundary (N.49 ↔ N.51), so acceptance in `remeasure` additionally holds a ±1px hysteresis band
 * (implementation-review F2): only a >1px change in a rounded dimension replaces the accepted
 * size and the key. */
function measuredViewport(el: HTMLElement): { width: number; height: number } {
  const rect = el.getBoundingClientRect();
  return {
    width: Math.round(rect.width) || COLONY_NOMINAL_VIEWPORT.width,
    height: Math.round(rect.height) || COLONY_NOMINAL_VIEWPORT.height,
  };
}

// Camera-control zoom bounds + rail step (camera-pan-zoom): mirrors `Match.tsx`'s own
// constants exactly — the same absolute zoom axis (`Camera.scale`), just a different
// nominal starting point (`COLONY_CLOSE_SCALE` = 1.0 vs `ESTABLISHING_CAMERA.scale` = 0.2).
const CAMERA_ZOOM_STEP = 1.5;
const CAMERA_ZOOM_MIN = 0.1;
const CAMERA_ZOOM_MAX = 4;
const CAMERA_ZOOM_BOUNDS: CameraGestureBounds = { minScale: CAMERA_ZOOM_MIN, maxScale: CAMERA_ZOOM_MAX };
// Clearance for the CameraControl rail/tier-chip above the full-width bee-type dock
// (`bottom:18` + its own padding/button height) — an implementation-time layout finding
// (design.md, Group 6), not present in the original plan.
const CAMERA_CONTROL_BOTTOM_OFFSET = 90;
// Clearance for the bee inspect panel (bee-inspect-panel): above BOTH the bee-type dock and
// the camera rail's bottom-left tier chip, which sits at `CAMERA_CONTROL_BOTTOM_OFFSET` and
// is ~38px tall — so the panel stacks over it rather than on top of it.
const INSPECT_PANEL_BOTTOM_OFFSET = CAMERA_CONTROL_BOTTOM_OFFSET + 48;

/** The colony play view's back "game canvas" layer (design DD5/DD6/DD7,
 * `colony-field-canvas`): the existing `InMatchCanvas` mounted absolutely behind the chrome,
 * reusing the one root-owned session and rendering close-tier centred on the own hive.
 * The hive `Camera` and the Close subscription viewport derive from **one** measured container
 * size (design DD9 / implementation-review F2): the camera is `hiveCloseCamera` over it, the
 * world viewport is `screenToWorld` of its corners. The container is measured once at mount
 * (`getBoundingClientRect`) and re-measured on resize (`ResizeObserver`). `InMatchCanvas` is
 * ADDITIONALLY keyed on the measured size (unchanged): a resize still fully remounts it (a
 * fresh camera capture at the PixiJS renderer's own resize granularity, `client/src/canvas/*`'s
 * concern, untouched) — but within one measured size, the camera now ALSO reacts to the
 * controlled pan/zoom delta below via `InMatchCanvas`'s reactive `camera` prop (camera-pan-zoom,
 * design.md D3/D4), which the pre-camera-pan-zoom "no reactive-camera refactor" note (DD7) had
 * left as a fixed one-shot capture.
 *
 * Interactive camera pan/zoom (camera-pan-zoom, `client-colony` "Interactive camera pan/zoom on
 * the Colony canvas"): available to the active player unconditionally (Colony has no
 * spectator/active split — it is the active player's own screen). A controlled `{centre, zoom}`
 * delta — seeded at the hive/`COLONY_CLOSE_SCALE` establishing shot and reset there on any
 * resize-triggered remount (architecture-review F4/D-127) — drives both the rendered `Camera`
 * (via the shared `cameraForZoomCentre`) and the Close-tier subscription viewport, exactly
 * mirroring `Match.tsx`'s own controlled camera. Colony's zoom is free across its full
 * `CAMERA_ZOOM_MIN..CAMERA_ZOOM_MAX` range: zooming out retains complete detail and switches
 * bees and flowers to symbols, but never navigates — the explicit "View match" HUD control
 * (on `Colony` itself, below) is the only way to `/match`.
 *
 * Tap-to-inspect + follow mode (bee-inspect-panel, `client-colony` "Tapping a bee opens the
 * inspect panel" / "Follow mode tracks the selected bee") also live here, because this is
 * where the controlled camera lives: picking needs the same `{centre, zoom}`-derived `Camera`
 * the renderer is handed, and follow drives that same state rather than installing a second
 * camera. Picking runs off the `WorldSnapshot` already in the store — `canvas/renderer.ts` is
 * not touched — through the pure `canvas/pick.ts` seam. */
function ColonyFieldUnderlay({
  underlay,
  tierForZoom = defaultTierForZoom,
  bottomChromeOffset = 0,
}: {
  underlay: ColonyUnderlaySeams;
  tierForZoom?: (zoom: number, current: Tier) => Tier;
  /** Extra clearance the host screen's own bottom chrome takes. The pvp prototype pins a prompt
   * bar under the bee-type dock, which pushes the dock up by exactly this much; without it the
   * camera rail and the inspect panel would sit under the dock instead of above it. */
  bottomChromeOffset?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    // Only replace the size object when a dimension changes by MORE than 1px, so a redundant
    // ResizeObserver notification (many implementations fire an initial one on `observe`) and a
    // half-pixel-boundary rounding flip (N.49 ↔ N.51 rounds N ↔ N+1) alike keep the accepted
    // size — and therefore the `InMatchCanvas` key — stable. A genuine resize clears the band
    // immediately; a slow drag tracks it in >1px steps.
    const remeasure = () => {
      const next = measuredViewport(el);
      setViewport((prev) =>
        prev !== null && Math.abs(prev.width - next.width) <= 1 && Math.abs(prev.height - next.height) <= 1
          ? prev
          : next,
      );
    };
    remeasure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(remeasure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // The controlled camera delta (camera-pan-zoom, design.md D3): seeded at the hive,
  // `COLONY_CLOSE_SCALE` establishing shot. Reset to that same identity whenever the
  // measured `viewport` changes (mount, or any resize) — architecture-review F4/D-127: the
  // recomputed base camera already reframes for the new size, so a stale delta is never
  // carried forward.
  const [cameraView, setCameraView] = useState<{ centre: [number, number]; zoom: number }>({
    centre: underlay.ownHive,
    zoom: COLONY_CLOSE_SCALE,
  });
  useEffect(() => {
    // Bail out to the SAME state object (React skips the re-render) when the values are
    // already at identity — true on the very first viewport measurement (the `useState`
    // initializer already seeded this exact value), so this effect only actually resets
    // (and only then re-fires the camera+viewport LOD-request effect below) on a GENUINE
    // resize after a prior pan/zoom moved `cameraView` away from identity.
    setCameraView((prev) =>
      prev.zoom === COLONY_CLOSE_SCALE && prev.centre[0] === underlay.ownHive[0] && prev.centre[1] === underlay.ownHive[1]
        ? prev
        : { centre: underlay.ownHive, zoom: COLONY_CLOSE_SCALE },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport]);

  const camera = viewport === null ? null : cameraForZoomCentre(cameraView.centre, cameraView.zoom, viewport);

  // Reassert the complete detail tier on route entry. This is idempotent in production and
  // keeps the route contract explicit for injected/test LOD seams.
  useEffect(() => {
    underlay.lod.requestTier(Tier.Close);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The detail subscription is re-issued whenever the measured size or controlled camera
  // changes. `setZoom` remains a seam but production pins it to Close; symbol mode draws one
  // dot per bee and flower at overview scales. Colony's zoom remains free across its full range. The
  // LodController's own coalescing debounce still collapses the mount effect's
  // `requestTier(Close)` above and this effect's first `setViewport` into one Subscribe, so
  // the camera updates still coalesce into one Subscribe.
  useEffect(() => {
    if (camera === null || viewport === null) return;
    const min = screenToWorld(camera, { x: 0, y: 0 });
    const max = screenToWorld(camera, { x: viewport.width, y: viewport.height });
    underlay.lod.setZoom(cameraView.zoom);
    underlay.lod.setViewport({ min, max });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, cameraView]);

  // --- tap-to-inspect + follow mode (bee-inspect-panel) --------------------------------
  //
  // `selectedBeeId` is the only durable selection state; the rendered `inspect` model is
  // recomputed from each accepted world frame by the subscription below (never held as a
  // stale copy), and `following` is a separate latch so a dismiss/re-select starts unfollowed.
  const [selectedBeeId, setSelectedBeeId] = useState<number | null>(null);
  const [following, setFollowing] = useState(false);
  const [inspect, setInspect] = useState<BeeInspectModel | null>(null);
  const ownColonyId = useClientStore((s) => s.colonyId);
  const colonyMeta = useClientStore((s) => s.colonyMeta);
  const streamEpoch = useClientStore((s) => s.streamEpoch);
  // `following` is read inside the store subscription, which must NOT resubscribe every time
  // the latch flips (that would tear down and rebuild the subscription mid-frame).
  const followingRef = useRef(false);
  followingRef.current = following;

  // The own colony's roster, the only type list whose names are knowable (an enemy's `typeId`
  // indexes ITS colony's private list, DESIGN §5). Memoized so it is a stable effect dep.
  const ownTypes: InspectTypeRef[] = useMemo(
    () =>
      (colonyMeta?.types ?? []).map((t, i) => ({
        typeId: t.typeId,
        name: t.name,
        colour: colorForType(t.name, i),
      })),
    [colonyMeta],
  );

  // A stream discontinuity (reconnect, or recovery from a sequence gap) drops the selection —
  // the same signal that already resets the corpse/facing/dance trackers (UI.md §8). The
  // retained id would otherwise re-bind to whatever entity holds it after the reset.
  useEffect(() => {
    setSelectedBeeId(null);
    setFollowing(false);
  }, [streamEpoch]);

  // One store subscription, live only while a bee is selected — so an unselected screen keeps
  // exactly its pre-change re-render profile (the underlay re-rendering at the ~10 Hz frame
  // rate would hand `InMatchCanvas` a fresh `camera` identity every frame and double-draw).
  useEffect(() => {
    if (selectedBeeId === null) {
      setInspect(null);
      return;
    }
    function apply(world: WorldSnapshot | null): void {
      // A legacy/replay Wide frame
      // carries no individual bees at all — the selected bee's absence here is the TIER, not
      // the bee actually disappearing, so it must not clear the panel, drop follow, or move
      // the camera (bee-inspect-panel review finding: "zooming out ends follow when the LOD
      // switches to Wide"). Leave the inspect model, selection, and follow latch exactly as
      // they were; the next accepted CLOSE frame resolves whether the bee is still there.
      if (world !== null && world.tier !== Tier.Close) {
        return;
      }
      const bee = world?.bees.get(selectedBeeId!);
      setInspect(
        beeInspectModel(selectedBeeId!, bee, {
          ownColonyId,
          ownTypes,
          dance: world?.dances.get(selectedBeeId!) ?? null,
        }),
      );
      if (bee === undefined) {
        // Gone from an accepted CLOSE frame: died, left the viewport, or dropped by the close
        // cap — indistinguishable on the wire (UI.md §8). Following an entity we can no longer
        // see is meaningless, so the latch drops with it.
        setFollowing(false);
        return;
      }
      if (followingRef.current) {
        setCameraView((prev) => ({ centre: [bee.position[0], bee.position[1]], zoom: prev.zoom }));
      }
    }
    apply(useClientStore.getState().currentWorld);
    // The same "either the reference changed or the seq advanced" predicate every other world
    // consumer here uses (`InMatchCanvas`, `Match`): a delta stream mutates one snapshot in
    // place (stable reference, advancing seq) while a reconnect keyframe installs a new
    // reference whose seq may have reset.
    return useClientStore.subscribe((state, prev) => {
      if (
        state.currentWorld !== null &&
        (state.currentWorld !== prev.currentWorld || state.lastSeq !== prev.lastSeq)
      ) {
        apply(state.currentWorld);
      }
    });
  }, [selectedBeeId, ownColonyId, ownTypes]);

  function dismissInspect(): void {
    setSelectedBeeId(null);
    setFollowing(false);
  }

  function toggleFollow(): void {
    setFollowing((wasFollowing) => {
      if (wasFollowing) return false;
      // Engaging snaps to the bee's current position immediately rather than waiting for the
      // next ~10 Hz frame, so the button feels like it did something.
      const bee = selectedBeeId === null ? undefined : useClientStore.getState().currentWorld?.bees.get(selectedBeeId);
      if (bee === undefined) return false;
      setCameraView((prev) => ({ centre: [bee.position[0], bee.position[1]], zoom: prev.zoom }));
      return true;
    });
  }

  function zoomBy(factor: number): void {
    // Zoom deliberately does NOT break follow — zooming while tracking a bee is the point;
    // the next frame simply re-centres at the new zoom.
    const zoom = Math.min(CAMERA_ZOOM_MAX, Math.max(CAMERA_ZOOM_MIN, cameraView.zoom * factor));
    setCameraView({ centre: cameraView.centre, zoom });
  }

  function resetCamera(): void {
    // An explicit camera command AWAY from the bee: follow would otherwise drag the view
    // straight back on the next frame.
    setFollowing(false);
    setCameraView({ centre: underlay.ownHive, zoom: COLONY_CLOSE_SCALE });
  }

  // The gesture handlers passed to `useCanvasCameraGestures` (mirrors `Match.tsx`'s own
  // stable-ref pattern, design.md D1): a STABLE object whose methods are reassigned every
  // render, so the hook's listener-attach effect never churns on a gesture-driven re-render.
  const gestureHandlersRef = useRef<CanvasCameraGestureHandlers>({
    onZoom: () => {},
    onPan: () => {},
    onTap: () => {},
  });
  gestureHandlersRef.current.onZoom = (screenPoint, factor) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return; // Unmeasured container: no jsdom safety net for gesture pixel geometry (D-086).
    }
    const localPoint = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, { width: rect.width, height: rect.height });
    const next = zoomAboutScreenPoint(current, localPoint, factor, CAMERA_ZOOM_BOUNDS);
    const nextCentre = screenToWorld(next, { x: rect.width / 2, y: rect.height / 2 });
    setCameraView({ centre: nextCentre, zoom: next.scale });
  };
  gestureHandlersRef.current.onPan = (dxPx, dyPx) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return;
    }
    // A deliberate pan is the player taking the camera back: follow must not snap it away
    // again on the next frame (bee-inspect-panel).
    setFollowing(false);
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, { width: rect.width, height: rect.height });
    const panned = panByScreenDelta(current, dxPx, dyPx);
    const nextCentre = screenToWorld(panned, { x: rect.width / 2, y: rect.height / 2 });
    setCameraView({ centre: nextCentre, zoom: cameraView.zoom });
  };
  gestureHandlersRef.current.onTap = (screenPoint) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0 || rect.height <= 0) {
      return; // Unmeasured container: no jsdom safety net for tap pixel geometry (D-086).
    }
    const world = useClientStore.getState().currentWorld;
    if (world === null) return;
    const local = { x: screenPoint.x - rect.left, y: screenPoint.y - rect.top };
    const current = cameraForZoomCentre(cameraView.centre, cameraView.zoom, { width: rect.width, height: rect.height });
    const picked = pickEntityAt(local, current, world.bees.values());
    // A tap that hits nothing deliberately LEAVES the current selection alone. The field is
    // a busy moving surface, so a miss is far likelier than a deliberate deselect, and the
    // panel carries an explicit ≥44px dismiss.
    if (picked === null) return;
    setSelectedBeeId(picked);
    setFollowing(false);
  };
  // Suppression (non-passive wheel + preventDefault + pointer capture) is unconditional;
  // `enabled=true` because Colony always drives the camera for the active player (no
  // spectator gate, unlike Match).
  useCanvasCameraGestures(containerRef, gestureHandlersRef.current, true);

  return (
    <div
      ref={containerRef}
      data-testid="colony-field-underlay"
      style={{ position: 'absolute', inset: 0, zIndex: 0, touchAction: 'none' }}
    >
      {/* The colony field reuses `InMatchCanvas` verbatim (design DD5): the additive
          render-input context (design D9) is fed from the store selectors inside
          `InMatchCanvas` — the shared canvas host for both the /play view and this
          /colony underlay — so the colony screen's canvas receives `season` / own-hive
          anchor / own-colony lifecycle / discontinuity counters without this component
          plumbing them itself. The colony-screen-only anchor drawing that consumes the
          anchor lands in group 8. `camera` is now reactive (camera-pan-zoom, design.md D4):
          panning/zooming redraws without remounting `InMatchCanvas` (the `key` below still
          remounts on an actual RESIZE, for the PixiJS renderer's own resize granularity). */}
      {camera !== null && (
        <InMatchCanvas
          key={`${viewport!.width}x${viewport!.height}`}
          sessionKey={underlay.sessionKey}
          createRenderer={underlay.createRenderer}
          camera={camera}
          selectedBeeId={selectedBeeId}
        />
      )}

      {/* Interactive camera pan/zoom HUD (camera-pan-zoom): reused verbatim from Match's kit
          component, positioned against THIS underlay's own inset:0 box (which fills the same
          screen area the `colony-screen` root does) — a `bottomOffset` clears the full-width
          bee-type dock the Match screen has no equivalent of. `data-gesture-chrome`
          (bee-inspect-panel) marks the whole control as chrome for the gesture adapter, so a
          press on the (non-button) tier chip cannot also pick the bee underneath it. */}
      {camera !== null && (
        <div data-gesture-chrome="">
          <CameraControl
            zoom={cameraView.zoom}
            tier={tierForZoom(cameraView.zoom, Tier.Close)}
            onZoomIn={() => zoomBy(CAMERA_ZOOM_STEP)}
            onZoomOut={() => zoomBy(1 / CAMERA_ZOOM_STEP)}
            onReset={resetCamera}
            bottomOffset={CAMERA_CONTROL_BOTTOM_OFFSET + bottomChromeOffset}
          />
        </div>
      )}

      {/* The bee inspect panel (bee-inspect-panel, UI.md §4): opens on a canvas tap that
          picks a bee, sits clear of both the bee-type dock and the camera rail's tier chip,
          and marks itself as gesture chrome so a press on it never re-picks the field. */}
      {inspect !== null && (
        <BeeInspectPanel
          model={inspect}
          following={following}
          onToggleFollow={toggleFollow}
          onDismiss={dismissInspect}
          bottomOffset={INSPECT_PANEL_BOTTOM_OFFSET + bottomChromeOffset}
        />
      )}
    </div>
  );
}

/** Inert fallback controller (never opens/sends): used only when a mount
 * supplies no `controller` at all (the still-unwired production route, before
 * task group 9). Every dispatch definitely refuses, exactly like a connection
 * that was never opened — never a silent no-op that looks like success. */
const INERT_CONTROLLER: ColonyController = {
  dispatch: () => 'not-ready',
};

/** The default once-per-browser onboarding "seen" adapter (design D-Persist),
 * backed by `window.localStorage`. The shim references `window` lazily and every
 * access is guarded inside the adapter, so constructing it at module load is safe
 * even where `window`/`localStorage` is unavailable (it degrades to "not seen").
 * Tests inject an in-memory double via the `onboardingSeenStore` prop instead. */
const WINDOW_ONBOARDING_SEEN_STORE: OnboardingSeenStore = createOnboardingSeenStore({
  getItem: (key) => window.localStorage.getItem(key),
  setItem: (key, value) => window.localStorage.setItem(key, value),
  removeItem: (key) => window.localStorage.removeItem(key),
});

// The match DURATION has no wire source (tick-rate-single-source task 5.5): it stays a fixed
// display assumption, consumed from the generated mirror (**fallback-permitted** — no request
// ever publishes it) so the exported constant is not dead code the `--check` ratchet guards for
// nothing. The tick RATE does have a wire source (`manifest.tickRateHz`) and is
// **wire-required** (E4): every site below takes it as a parameter sourced from the manifest,
// with no defaulted fallback.
const PROMPT_COOLDOWN_SECONDS = 60;
const TYPE_CAP = 12; // DESIGN §14

// **`PRESET_VISUALS` / `extensionPaletteIndex` / `spriteForType` / `colorForType` now live in
// `ui/rounds/type-visuals.ts`** (`pvp-ui-fixes` W1) and are imported above rather than declared
// here. They were lifted out of this file once already, for the sandbox screen's chips, and that
// move left a deliberate duplicate — "a duplicated five-row table is a smaller risk than a
// cross-lane edit", as that file's header recorded, with the collapse noted as owed. The owed
// collapse is now done, because the duplication became the bug's shape: the sprite URLs were bare
// relative names that broke on any two-segment route, and the same wrong value had to be corrected
// in two tables. Nothing about the mapping changed — the module is a verbatim carrier of what was
// here, plus the `abbrev` field this screen does not read.

/** Dock/header live count (OQ3, resolved): the sum of this type's own-colony
 * `StateHistogram` buckets — the finest own-colony per-type signal the shipped
 * surface carries. `0` (not fabricated) when no histogram has arrived yet. */
function countForType(histogram: StateHistogram | null, typeId: number): number {
  const perType = histogram?.perType.find((t) => t.typeId === typeId);
  if (perType === undefined) return 0;
  return perType.buckets.reduce((sum, b) => sum + b.count, 0);
}

const BUFF_OPTIONS: { value: BuffName; label: string }[] = [
  { value: BuffName.Health, label: 'health' },
  { value: BuffName.FoodConsumption, label: 'food-consumption' },
  { value: BuffName.Capacity, label: 'capacity' },
  { value: BuffName.Strength, label: 'strength' },
  { value: BuffName.Speed, label: 'speed' },
  { value: BuffName.Building, label: 'building' },
  { value: BuffName.Range, label: 'range' },
];

function rejectionNoticeText(reason: RejectReason): string {
  switch (reason.kind) {
    case 'rateLimited':
      return 'Still cooling down — try again shortly';
    case 'stale':
      return 'Out of date — try again';
    case 'invalid':
      return "That didn't go through — try again";
    case 'unauthorized':
      return 'Not authorized for that action';
    case 'upgrade':
      // Never actually reaches `onMutationRejected` (client-wire: Connection
      // handles Rejected{upgrade} terminally itself) — handled for exhaustiveness.
      return 'Client upgrade required';
    case 'compileFailed':
      // Short, tab-agnostic hint (architecture-review adopted decision, client-colony
      // "Mutation dispatch seam and rejection surfacing"): the full diagnostics list is
      // the Code tab's own HandEdit banner's job, never duplicated here.
      return "Your hand edit didn't compile — see the Code tab";
    case 'turnInProgress':
      // Rounds-only (`rounds-http.ts`'s `handEditRefusalFrom`): a Beekeeper turn holds the
      // sandbox's in-flight slot, so the hand edit was refused rather than queued — the Code tab
      // reopens edit mode with the draft preserved on this exact kind, same as `stale`.
      return 'A Beekeeper turn is running — try again once it finishes';
  }
}

const CLOSE_BUTTON_STYLE: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 22,
  border: '1px solid var(--line-strong)',
  background: 'var(--surface-card)',
  color: 'var(--ink-2)',
  fontSize: 16,
  cursor: 'pointer',
  flex: 'none',
};

// Bespoke tab strip (no dedicated kit component ships one; the `.tabs`/`.tab`
// rule lives directly in `prototype/ui_kits/waggle-app/colony.html`). Bumped
// to a 44px min-height here — the mock's own `.tab` rule is 38px, below the
// design system's normative ≥44px hit target (UI.md §2) — same fix pattern as
// task 6.2/7.2's Sheet-close-button/VersionRow-nav fixes, mirrored back into
// the mock's `.tab` rule in this same change (prototype/CLAUDE.md sync contract).
const TAB_HIT_TARGET = 44;

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        minHeight: TAB_HIT_TARGET,
        border: 'none',
        borderRadius: 'var(--r-pill)',
        background: active ? 'var(--surface-card)' : 'transparent',
        boxShadow: active ? 'var(--shadow-card)' : 'none',
        fontFamily: 'var(--font-display)',
        fontWeight: 500,
        fontSize: 14,
        color: active ? 'var(--ink-1)' : 'var(--ink-3)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

export function Colony({
  controller = INERT_CONTROLLER,
  underlay,
  sound,
  sessionPhase,
  onboardingSeenStore = WINDOW_ONBOARDING_SEEN_STORE,
  // `onNavigate` is deliberately NOT destructured — see its prop doc.
  tierForZoom,
  pvpChat,
}: ColonyProps) {
  const ownHive = useClientStore((s) => s.ownHive);
  const manifest = useClientStore((s) => s.manifest);
  const colonyMeta = useClientStore((s) => s.colonyMeta);
  const promptStatusByType = useClientStore((s) => s.promptStatusByType);
  const histogram = useClientStore((s) => s.histogram);
  const matchState = useClientStore((s) => s.matchState);
  const versionCache = useClientStore((s) => s.versionCache);
  const provenanceLog = useClientStore((s) => s.provenanceLog);
  const colonySelection = useClientStore((s) => s.colonySelection);
  const role = useClientStore((s) => s.role);
  const mutationRejection = useClientStore((s) => s.mutationRejection);
  const ownScoreboardRow = useClientStore((s) => selectOwnScoreboardRow(s));
  const ownEliminated = useClientStore((s) => selectOwnEliminated(s));
  const selectColonyType = useClientStore((s) => s.selectColonyType);
  const setColonyActiveTab = useClientStore((s) => s.setColonyActiveTab);
  const setViewedVersion = useClientStore((s) => s.setViewedVersion);
  const dismissMutationRejection = useClientStore((s) => s.dismissMutationRejection);
  const hasSeenColonyIntro = useClientStore((s) => s.hasSeenColonyIntro);
  const guideStep = useClientStore((s) => s.guideStep);
  const setHasSeenColonyIntro = useClientStore((s) => s.setHasSeenColonyIntro);
  const setGuideStep = useClientStore((s) => s.setGuideStep);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBuff, setCreateBuff] = useState<BuffName>(BuffName.Strength);
  const [createBirthType, setCreateBirthType] = useState(false);
  // A not-ready CreateType dispatch (implementation-review F4): the previous
  // code ignored the dispatch result, always closing the sheet — a not-ready
  // send therefore failed silently, with no feedback and no retry surface. Keep
  // the sheet open and show a notice instead, mirroring the prompt box's own
  // not-ready handling (client-colony "Mutation dispatch seam and rejection
  // surfacing": "surfaced ... without dropping the stream").
  const [createNotice, setCreateNotice] = useState<string | null>(null);
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  // Local optimistic pending state (client-colony "Prompt box status cycle,
  // advisory cooldown, and diagnostics": "enter its local pending state only on
  // a confirmed dispatch" — implementation-review F3). The type a `sent` prompt
  // dispatch is awaiting a `PromptStatus` for; cleared once a definite (non-idle)
  // status for that type is observed, so Send cannot be tapped twice in the gap
  // between `dispatch` returning `sent` and the server's first `Generating`.
  const [sendingTypeId, setSendingTypeId] = useState<number | null>(null);

  const disabled = role === 'spectator' || ownEliminated;

  // First-run guidance gate (design D-Gate): shown ONLY for an in-match player whose own
  // colony is not eliminated and whose browser has not acknowledged the current guide
  // version. The `!ownEliminated` term consults the SAME `selectOwnEliminated` signal the
  // mutation affordances use (`ownEliminated` above), not `role` alone (artifacts-review
  // F4), so the guide stays excluded even in any transient state where `role` has not yet
  // reconciled to spectator — its guidance would be unactionable there (UI.md §3).
  const showGuide =
    role === 'player' && !ownEliminated && sessionPhase === 'in-match' && !hasSeenColonyIntro;

  // Dismiss ("Got it"/"Skip"): persist the acknowledgement to the durable
  // `waggle-onboarding-seen` record and flip the mirror true so the gate closes and the
  // guide never re-shows (design D-Gate). This is the ONLY exit that writes storage.
  function dismissGuide(): void {
    onboardingSeenStore.markGuideSeen();
    setHasSeenColonyIntro(true);
  }

  const types = colonyMeta?.types ?? [];
  const selectedType: TypeMeta | null =
    types.find((t) => t.typeId === colonySelection.selectedTypeId) ?? null;
  const selectedIndex = selectedType ? types.indexOf(selectedType) : -1;

  const nowTick = matchState?.tick ?? colonyMeta?.tick ?? 0;
  // `manifest` is `null` only in the brief pre-Welcome window, mirroring `matchState`. No
  // fallback rate (client-tick-rate-provenance E4): absent a real `tickRateHz` there is nothing
  // honest to compute a total from, so it reads as "no progress yet" (0), the same as
  // `matchState` being absent already reads.
  const totalMatchTicks = manifest !== null ? MATCH_DURATION_MIN * 60 * manifest.tickRateHz : null;
  const progress =
    matchState && totalMatchTicks !== null ? 1 - matchState.state.clockRemainingTicks / totalMatchTicks : 0;

  // The PromptBox status actually shown for the selected type: the server's own
  // `PromptStatus` once one has arrived, or — while a `sent` dispatch is still
  // awaiting its first status — a local `generating` placeholder so Send cannot
  // be tapped again in that gap (client-colony "enter its local pending state
  // only on a confirmed dispatch"; implementation-review F3).
  const serverPromptStatus = selectedType ? promptStatusByType[selectedType.typeId]?.status : undefined;
  const effectivePromptStatus: PromptBoxStatus =
    selectedType !== null &&
    sendingTypeId === selectedType.typeId &&
    (serverPromptStatus === undefined || serverPromptStatus.kind === 'idle')
      ? { kind: 'generating' }
      : ((serverPromptStatus as PromptBoxStatus | undefined) ?? { kind: 'idle' });

  // Starter-prompt visibility gate (design D-Starter, architecture-review F1/F3): the
  // chip row shows only when a send would actually be accepted. Colony derives
  // busy/cooldown PER SELECTED TYPE, so that per-type signal is blind to a
  // type-A-accepted → switch-to-idle-B cooldown; a PLAYER-WIDE advisory cooldown
  // selector (`playerWideCooldownSeconds`, the max UNEXPIRED accepted-cooldown across
  // ALL types) closes that gap BEFORE any send bounces, and the authoritative
  // `notReadyNotice`/`Rejected{rateLimited}` signal covers the post-bounce path. The
  // draft-empty condition is owned inside `PromptBox` (`text` is its internal state).
  //
  // The cooldown is tick-derived, because in live mode the tick **is** the clock. The wall-clock
  // fork this used to carry belonged to the rounds sandbox, which has no running tick; it left with
  // the rest of the rounds path (`sandbox-screen-consolidation` task 8.2), and the live derivation
  // below is what it always was.
  //
  // `tickRateHz` is `null` only in the brief pre-Welcome window (mirrors `manifest` itself,
  // client-tick-rate-provenance E4 — no defaulted fallback): with no real rate to divide by,
  // every cooldown below reads as "not on cooldown" rather than a guessed countdown.
  const tickRateHz = manifest?.tickRateHz ?? null;
  const selectedCooldownSeconds =
    selectedType && tickRateHz !== null
      ? promptCooldownSeconds(promptStatusByType[selectedType.typeId], nowTick, tickRateHz)
      : 0;
  const promptNotReadyNotice =
    localNotice ?? (mutationRejection ? rejectionNoticeText(mutationRejection) : undefined);
  const selectedTypeBusy =
    effectivePromptStatus.kind === 'generating' ||
    effectivePromptStatus.kind === 'repairing' ||
    (effectivePromptStatus.kind === 'accepted' && selectedCooldownSeconds > 0);
  const startersSuppressed =
    disabled ||
    selectedTypeBusy ||
    (tickRateHz !== null && playerWideCooldownSeconds(promptStatusByType, nowTick, tickRateHz) > 0) ||
    promptNotReadyNotice !== undefined;

  // Clears the optimistic pending flag once the server delivers a definite
  // (non-idle) status for the awaited type — a real `Generating`/`Repairing`/
  // `Accepted`/`Failed`/`Stale` supersedes the local placeholder, and clearing it
  // here (rather than never) stops a stale flag from re-surfacing as a fabricated
  // "generating" if the same type is reopened later while genuinely idle.
  //
  // A **rejection** releases it too. A prompt the server refuses (cooling down, a generation
  // already in flight, unauthorized, a dead transport) produces no `PromptStatus` at all, so a
  // definite status is never coming and the flag would strand the box in a fabricated "Beekeeper
  // thinking…" with Send disabled and nothing left to clear it. `onSend` dismisses any standing
  // rejection before dispatching, so a rejection observed while a send is outstanding is
  // necessarily that send's — as close to correlation as the uncorrelated rejection surface (D8)
  // allows, and the failure it prevents is unrecoverable rather than cosmetic.
  useEffect(() => {
    if (sendingTypeId === null) return;
    const status = promptStatusByType[sendingTypeId]?.status;
    if ((status !== undefined && status.kind !== 'idle') || mutationRejection !== null) {
      setSendingTypeId(null);
    }
  }, [sendingTypeId, promptStatusByType, mutationRejection]);

  // Re-seed the durable `hasSeenColonyIntro` mirror from the versioned
  // `waggle-onboarding-seen` record on Colony mount (design D-Slice, new-match entry
  // re-seed): after a between-match `reset()` transiently set the mirror `false`,
  // entering `/colony` re-derives "seen" from the durable authority, so the gate never
  // re-shows the guide off a stale mirror. Guarded inside the adapter (degrades to "not
  // seen"). Boot's first seed lives in `main.tsx`; `reset()`/`resetForNewMatch()` stay
  // unchanged (additive-only).
  //
  // `useLayoutEffect`, not `useEffect` (g4 review note): the re-seed must correct the
  // mirror BEFORE the browser paints. A returning (already-seen) player entering a
  // later match mounts with the transient post-`reset()` `false` mirror, so the first
  // render computes `showGuide === true`; a post-paint `useEffect` would let the guide
  // flash for one frame before flipping it false. `useLayoutEffect`'s synchronous
  // `setHasSeenColonyIntro` re-render runs before paint, so `showGuide` is already
  // false the first time anything is painted. A genuine first-time player
  // (`hasSeenGuide() === false`) re-seeds to `false` — a no-op — and still sees it.
  useLayoutEffect(() => {
    setHasSeenColonyIntro(onboardingSeenStore.hasSeenGuide());
  }, [onboardingSeenStore, setHasSeenColonyIntro]);

  // Discard ephemeral guide progress on Colony route-exit/unmount (design D-Slice,
  // artifacts-review F2): `guideStep` is store-held (it survives an unmount), so a
  // same-match remount would otherwise resume a stale mid-sequence step. The
  // non-persisting `resetGuideProgress` returns it to step one WITHOUT any
  // `localStorage` write — only an explicit dismiss persists.
  useEffect(() => {
    return () => {
      useClientStore.getState().resetGuideProgress();
    };
  }, []);

  // Also discard ephemeral progress the moment the first-run gate CLOSES without a
  // dismissal while Colony stays mounted — the in-match ELIMINATION path
  // (implementation-review F1). Elimination flips `ownEliminated` (and reconciles
  // `role → spectator`), so `showGuide` goes false and the FirstRunGuide overlay
  // unmounts, but Colony itself does NOT, so the `[]`-deps route-exit cleanup above
  // never fires and `guideStep` would linger mid-sequence. This effect wires the
  // discard to the gate-close event itself so the elimination scenario's "ephemeral
  // progress is discarded without persisting" holds via real UI wiring, not only the
  // store action in isolation. Guarded to a genuine clear (non-initial progress) so a
  // returning/seen player's already-zero progress and the normal dismiss path cause no
  // redundant store write; never touches `localStorage` (only an explicit dismiss
  // persists).
  useEffect(() => {
    if (showGuide) return;
    const { guideStep: step, guideDismissed: dismissed } = useClientStore.getState();
    if (step !== 0 || dismissed) {
      useClientStore.getState().resetGuideProgress();
    }
  }, [showGuide]);

  function dispatch(mutation: ScriptingMutation): 'sent' | 'not-ready' {
    const result = controller.dispatch(mutation);
    return result;
  }

  // --- the pvp prototype's rounds-shaped prompt surface --------------------------------------
  //
  // Every binding below is inert without the seam: `useChatSeam(undefined)` answers `undefined`,
  // `pvpChat` guards every render site, and no effect below does anything. Kept unconditional
  // (rather than behind an early branch) because hooks cannot be called conditionally.
  const pvpChatSnapshot = useChatSeam(pvpChat);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptFocused, setPromptFocused] = useState(false);

  // One rehydrate per wired seam, the same boot call the rounds root makes: a reload mid-turn
  // re-attaches to the running turn rather than showing a transcript that looks finished.
  useEffect(() => {
    void pvpChat?.rehydrate();
  }, [pvpChat]);

  // The way back into a conversation whose popup has been closed. Derived from the same transcript
  // the overlay draws, so the two cannot disagree (`SandboxScreen`'s own note).
  // `view !== 'open'` — dismissed and minimised alike (`player-chat-and-outcome` **D-2**); while
  // minimised this row is the player's only report of a turn that is still running.
  const pvpThreadSummary =
    pvpChatSnapshot !== undefined && pvpChatSnapshot.view !== 'open'
      ? summarizeThread(pvpChatSnapshot.transcript)
      : null;
  const pvpPromptHeight =
    (promptFocused ? PROMPT_BAR_HEIGHT_FOCUSED : PROMPT_BAR_HEIGHT) +
    (pvpThreadSummary !== null ? PROMPT_BAR_THREAD_ROW_HEIGHT : 0);
  // Derived once, the same way `SandboxScreen.tsx` derives its own `chatPhase` — every gate below
  // (the composer, the reentry row's tone, the hand-edit withholding) reads this rather than
  // `pvpChatSnapshot?.transcript.running` alone, which only ever answers "has a `turnStarted` been
  // observed" and misses `submitting` (before any server frame) and `uncertain` (an ambiguous
  // outcome) entirely — the exact gap `beekeeper-progress-indicator` F2 closes on this screen.
  const pvpChatPhase: ChatPhase = pvpChatSnapshot?.phase ?? 'idle';

  /**
   * The colony document the `TypeSheet` slices, assembled from the live roster's own
   * `canonicalText` blocks (each already begins with its `type <name> buffs <buff>` header, so
   * `sliceType`/`replaceTypeSlice` find them). Live mode has no server-side working document —
   * that is a rounds sandbox concept — so this is the nearest true equivalent, and it deliberately
   * shows the **live head** of each type rather than `versionCache`'s viewed version: the version
   * strip beside it is what steps through history.
   */
  const pvpColonyDocument = types.map((t) => t.canonicalText).join('\n');

  function sendPvpPrompt(): void {
    const text = promptDraft.trim();
    if (text === '' || pvpChat === undefined || disabled) {
      return;
    }
    // Deliberately no `isChatBusy(pvpChatPhase)` guard here (`beekeeper-progress-indicator` F2):
    // `pvpChat.sendAndOpen` already refuses a second send at the channel level (`chat-channel.ts`'s
    // own guard, shared by every surface), so re-checking busy-ness here would just be a second
    // place that guard could drift from — the PromptBar's `disabled` prop above is the visible echo
    // for this surface, not a second enforcement point.
    //
    // The scope travels **with** the message: the agent's unit of work is the whole colony, and
    // the open type is context for the request rather than a target it is confined to.
    pvpChat.sendAndOpen(text, selectedType?.name ?? null);
    setPromptDraft('');
    setPromptFocused(false);
  }

  function closeSheet(): void {
    selectColonyType(null);
    dismissMutationRejection();
  }

  function openCreateType(): void {
    if (disabled || types.length >= TYPE_CAP) return;
    setCreateName('');
    setCreateBuff(BuffName.Strength);
    setCreateBirthType(false);
    setCreateNotice(null);
    setCreateOpen(true);
  }

  function submitCreateType(): void {
    const name = createName.trim() || 'New type';
    const result = dispatch({
      type: 'createType',
      value: { name, buff: createBuff, initialProgram: { kind: 'preset', preset: 'blank' } },
    });
    if (result === 'not-ready') {
      // Keep the sheet open and surface the same result the player would see
      // sending a prompt while disconnected — never close over a send that
      // never went out, and never send the birth-type follow-up for a type
      // that was never created.
      setCreateNotice('Reconnecting — try again');
      return;
    }
    if (createBirthType) {
      // The new type's typeId is server-assigned (not known until the next
      // ColonyMeta) — SetBirthType targets by name, which the player just chose.
      dispatch({ type: 'setBirthType', value: { typeName: name } });
    }
    setCreateNotice(null);
    setCreateOpen(false);
  }

  // The live close-tier field underlay (design DD7): mounted behind the chrome ONLY once
  // `ownHive` is present in the store, so `InMatchCanvas`' one-shot camera capture is always
  // the correct hive camera — on the flagship (Welcome → /colony) and the reload (/colony
  // before the reconnect Welcome) paths alike. Chrome renders immediately either way.
  const showUnderlay = ownHive !== null && underlay !== undefined;

  return (
    // `overflow: 'hidden'` breaks the underlay's layout feedback edge
    // (colony-underlay-canvas-stability): the `InMatchCanvas` field canvas is `position:absolute;
    // inset:0` inside this box and transiently overflows it while a fresh PixiJS renderer
    // initialises on each mount. Without clipping, that overflow escaped to the page, raised a
    // page scrollbar that narrowed the underlay's measured container by ~16px, changed the
    // canvas `key`, and remounted the canvas — a self-sustaining loop leaking one WebGL context
    // per cycle so the field never painted (confirmed in-browser via dev:fake, D-086). Clipping
    // here keeps the transient overflow off the page. The route's height chain (`#root`→here,
    // both `height:100%`) is already resolved (D-085), so the box does not collapse either.
    <div
      data-testid="colony-screen"
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
    >
      {showUnderlay && (
        <ColonyFieldUnderlay
          underlay={underlay}
          tierForZoom={tierForZoom}
          // 0 without the pvp seam, so the shipped HUD geometry is untouched.
          bottomChromeOffset={pvpChat === undefined ? 0 : pvpPromptHeight}
        />
      )}

      {/* Top HUD (display-only, UI.md §4) */}
      <div
        style={{
          position: 'absolute',
          top: 14,
          left: 12,
          right: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pointerEvents: 'none',
        }}
      >
        {/* **No "View match" control** (`player-chat-and-outcome` **D-4**). It used to sit under
            the season clock, competing for the corner with the world the player is meant to be
            watching. `/match` is unchanged and still reachable by URL and by the presentation's own
            navigation, and the reciprocal — Match's `⌂ Back to my hive` — deliberately STAYS, so
            the path is one-way by decision rather than by accident. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <SeasonClock progress={Math.max(0, Math.min(1, progress))} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <ResourceCounter icon={HUD_ICONS.worker} value={ownScoreboardRow?.livingCount ?? 0} label="bees" />
          <ResourceCounter icon={HUD_ICONS.nectar} value={ownScoreboardRow?.pool ?? 0} label="nectar" />
          {/* SoundControl (client-audio DD6): persistent top-right HUD corner, just under the
              resource stack (placement sketch), at Colony↔Match parity. `pointerEvents: 'auto'`
              overrides this display-only HUD wrapper's `none` so the toggle is tappable;
              spectators keep it (sound is not a mutation). Mounts only once the audio seam is
              wired (task group 7) — additive, no screen-contract change. */}
          {sound && (
            <div data-testid="sound-control-mount" style={{ pointerEvents: 'auto' }}>
              <SoundControl lifecycle={sound.lifecycle} prefs={sound.prefs} />
            </div>
          )}
        </div>
      </div>

      {/* Bee-type dock */}
      <div
        data-testid="bee-type-dock"
        style={{
          position: 'absolute',
          left: 10,
          right: 10,
          // With the pvp prompt bar pinned along the bottom, the dock sits directly above it
          // rather than where the bar now is.
          bottom: pvpChat === undefined ? 18 : pvpPromptHeight + 8,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          padding: 6,
          background: 'var(--hud-bg)',
          border: '1.5px solid var(--hud-line)',
          borderRadius: 18,
          boxShadow: 'var(--shadow-hud)',
        }}
      >
        {types.map((t, i) => (
          <BeeTypeButton
            key={t.typeId}
            name={t.name}
            count={countForType(histogram, t.typeId)}
            color={colorForType(t.name, i)}
            sprite={spriteForType(t.name)}
            selected={t.typeId === colonySelection.selectedTypeId}
            alert={promptStatusByType[t.typeId]?.status.kind === 'failed'}
            onClick={() => selectColonyType(t.typeId)}
          />
        ))}
        <button
          data-testid="dock-add-button"
          disabled={disabled || types.length >= TYPE_CAP}
          title={
            types.length >= TYPE_CAP
              ? `Colony type limit reached (${TYPE_CAP} of ${TYPE_CAP})`
              : 'Create a bee type'
          }
          onClick={openCreateType}
          aria-label="New bee type"
          style={{
            width: 44,
            height: 44,
            flex: 'none',
            borderRadius: 'var(--r-md)',
            border: '1.5px dashed var(--hud-line)',
            background: 'transparent',
            color: 'var(--hud-text-dim)',
            fontSize: 20,
            fontFamily: 'var(--font-display)',
            cursor: disabled || types.length >= TYPE_CAP ? 'default' : 'pointer',
            opacity: disabled || types.length >= TYPE_CAP ? 0.4 : 1,
          }}
        >
          +
        </button>
      </div>

      {/* **The pvp prototype's inset bee-type popup**, in place of the scrim + `Sheet` below.
          Same subject, same version affordance (in the sheet's `toolbar` slot), no scrim — the
          field and the pinned prompt bar stay live underneath, which is the whole point of the
          rounds composition this prototype adopts. */}
      {selectedType && pvpChat !== undefined && (
        <TypeSheet
          typeId={selectedType.typeId}
          typeName={selectedType.name}
          color={colorForType(selectedType.name, selectedIndex)}
          sprite={spriteForType(selectedType.name)}
          buff={selectedType.buffName}
          birthType={colonyMeta?.birthType === selectedType.typeId}
          onSetBirthType={
            disabled
              ? undefined
              : () => dispatch({ type: 'setBirthType', value: { typeName: selectedType.name } })
          }
          colonyDocument={pvpColonyDocument}
          population={populationFromHistogram(histogram)}
          playheadLabel={tickRateHz === null ? undefined : clockLabel(nowTick, tickRateHz)}
          // Withheld for the whole of `busy` (`submitting`/`running`/`uncertain`), not merely while
          // `running`: a send that has not yet produced a `turnStarted`, or one whose outcome is
          // unknown, still means a turn may hold the sandbox, so hand editing must stay withheld.
          turnInFlight={isChatBusy(pvpChatPhase)}
          onSaveSlice={
            disabled
              ? undefined
              : (next) =>
                  // The sheet hands back one type's slice, header line included — which is exactly
                  // what live mode's per-type `HandEdit` carries, so it goes straight out. No
                  // whole-document write exists on this wire.
                  void dispatch({
                    type: 'handEdit',
                    value: {
                      typeName: selectedType.name,
                      programSource: next,
                      baseVersion: selectedType.activeVersion,
                    },
                  })
          }
          onClose={closeSheet}
          toolbar={
            <VersionBar
              colonyMeta={colonyMeta}
              versionCache={versionCache}
              provenanceLog={provenanceLog}
              promptStatusByType={promptStatusByType}
              typeId={selectedType.typeId}
              typeName={selectedType.name}
              viewedVersion={colonySelection.viewedVersion}
              setViewedVersion={setViewedVersion}
              disabled={disabled}
              nowTick={nowTick}
              tickRateHz={tickRateHz}
              dispatch={dispatch}
            />
          }
        />
      )}

      {/* Scrim + bee-type sheet */}
      {selectedType && pvpChat === undefined && (
        <>
          <div
            onClick={closeSheet}
            style={{ position: 'absolute', inset: 0, background: 'rgba(20,14,6,.35)' }}
          />
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '74%' }}>
            <Sheet style={{ height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <img
                    src={spriteForType(selectedType.name)}
                    alt={selectedType.name}
                    style={{ width: 44, height: 44, imageRendering: 'pixelated' }}
                  />
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 11 }}>
                    {countForType(histogram, selectedType.typeId)} alive
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 600,
                      fontSize: 22,
                      color: 'var(--ink-1)',
                    }}
                  >
                    {selectedType.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                    <Chip style={{ cursor: 'default' }}>{selectedType.buffName} ×2</Chip>
                    {colonyMeta?.birthType === selectedType.typeId ? (
                      <span
                        data-testid="birth-type-badge"
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSize: 11,
                          color: 'var(--accent-strong)',
                        }}
                      >
                        🐣 Birth type
                      </span>
                    ) : (
                      <button
                        data-testid="birth-type-button"
                        disabled={disabled}
                        onClick={() =>
                          dispatch({ type: 'setBirthType', value: { typeName: selectedType.name } })
                        }
                        style={{
                          minHeight: 44,
                          padding: '0 12px',
                          borderRadius: 'var(--r-pill)',
                          border: '1px solid var(--line-strong)',
                          background: 'var(--surface-card)',
                          color: 'var(--ink-3)',
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSize: 11,
                          cursor: disabled ? 'default' : 'pointer',
                          opacity: disabled ? 0.5 : 1,
                        }}
                      >
                        Set as birth type
                      </button>
                    )}
                  </div>
                </div>
                <button
                  data-testid="sheet-close"
                  onClick={closeSheet}
                  style={CLOSE_BUTTON_STYLE}
                >
                  ✕
                </button>
              </div>

              <VersionBar
                colonyMeta={colonyMeta}
                versionCache={versionCache}
                provenanceLog={provenanceLog}
                promptStatusByType={promptStatusByType}
                typeId={selectedType.typeId}
                typeName={selectedType.name}
                viewedVersion={colonySelection.viewedVersion}
                setViewedVersion={setViewedVersion}
                disabled={disabled}
                nowTick={nowTick}
                tickRateHz={tickRateHz}
                dispatch={dispatch}
              />

              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  background: 'var(--paper-1)',
                  borderRadius: 'var(--r-pill)',
                  padding: 4,
                  margin: '10px 0',
                }}
              >
                <TabButton
                  label="Flowchart"
                  active={colonySelection.activeTab === 'flowchart'}
                  onClick={() => setColonyActiveTab('flowchart')}
                />
                <TabButton
                  label="Code"
                  active={colonySelection.activeTab === 'code'}
                  onClick={() => setColonyActiveTab('code')}
                />
              </div>

              <ScriptBody
                key={selectedType.typeId}
                colonyMeta={colonyMeta}
                versionCache={versionCache}
                promptStatusByType={promptStatusByType}
                typeId={selectedType.typeId}
                typeName={selectedType.name}
                typeColor={colorForType(selectedType.name, selectedIndex)}
                viewedVersion={colonySelection.viewedVersion}
                activeTab={colonySelection.activeTab}
                disabled={disabled}
                mutationRejection={mutationRejection}
                dispatch={dispatch}
              />

              <PromptBox
                style={{ marginTop: 8 }}
                status={effectivePromptStatus}
                cooldownSeconds={selectedCooldownSeconds}
                notReadyNotice={promptNotReadyNotice}
                disabledReason={disabled ? 'Spectating — prompts are disabled' : undefined}
                starterPrompts={STARTER_PROMPTS}
                startersSuppressed={startersSuppressed}
                starterHint={STARTER_PROMPT_HINT}
                starterMicrocopy={STARTER_PROMPT_MICROCOPY}
                onSend={(text) => {
                  // A sealed phase / spectator session disables this whole box (`disabledReason`
                  // above); this guard is the belt to that suspenders — the entry point must never
                  // dispatch a generation regardless of how it was reached.
                  if (disabled) {
                    return;
                  }
                  // A one-shot generation dispatch. The rounds fork that used to sit here — open
                  // the chat overlay instead of submitting — went with the rest of the rounds path
                  // (task 8.2); it is the sandbox screen's pinned prompt now.
                  setLocalNotice(null);
                  dismissMutationRejection();
                  const result = dispatch({
                    type: 'prompt',
                    value: { typeName: selectedType.name, promptText: text },
                  });
                  if (result === 'not-ready') {
                    setLocalNotice('Reconnecting — try again');
                  } else {
                    setSendingTypeId(selectedType.typeId);
                  }
                }}
              />

            </Sheet>
          </div>
        </>
      )}

      {/* Create-type flow */}
      {createOpen && (
        <>
          <div
            onClick={() => setCreateOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(20,14,6,.35)', zIndex: 7 }}
          />
          <div
            data-testid="create-type-sheet"
            style={{
              position: 'absolute',
              left: 20,
              right: 20,
              top: '6%',
              bottom: '6%',
              overflowY: 'auto',
              background: 'var(--surface-app)',
              borderRadius: 'var(--r-lg)',
              boxShadow: 'var(--shadow-sheet)',
              padding: 16,
              zIndex: 8,
            }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18, marginBottom: 10 }}>
              Create a bee type
            </div>
            <label htmlFor="create-type-name" style={{ fontWeight: 800, fontSize: 10, textTransform: 'uppercase' }}>
              Name
            </label>
            <input
              id="create-type-name"
              data-testid="create-name-input"
              value={createName}
              maxLength={24}
              onChange={(e) => setCreateName(e.target.value)}
              style={{ width: '100%', minHeight: 44, display: 'block', marginBottom: 10 }}
            />
            <div style={{ fontWeight: 800, fontSize: 10, textTransform: 'uppercase', marginBottom: 6 }}>
              Boosted at (locked after creation)
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {BUFF_OPTIONS.map((b) => (
                <Chip
                  key={b.value}
                  selected={createBuff === b.value}
                  onClick={() => setCreateBuff(b.value)}
                >
                  {b.label}
                  {createBuff === b.value ? ' ×2 ✓' : ''}
                </Chip>
              ))}
            </div>
            <div
              onClick={() => setCreateBirthType(!createBirthType)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                readOnly
                checked={createBirthType}
                aria-label="Set as birth type"
              />
              <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Set as birth type</span>
            </div>
            <div
              data-testid="create-swatch"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                fontSize: 12,
                color: 'var(--text-muted)',
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 6,
                  borderRadius: 3,
                  background: `var(--bee-ext-${extensionPaletteIndex(types.length)})`,
                }}
              />
              <span>Type colour (extension palette · index {types.length})</span>
            </div>
            {createNotice && (
              <div
                data-testid="create-type-notice"
                style={{
                  marginTop: 10,
                  fontFamily: 'var(--font-body)',
                  fontSize: 12,
                  color: 'var(--danger)',
                  background: 'var(--danger-soft)',
                  borderRadius: 'var(--r-md)',
                  padding: '7px 10px',
                }}
              >
                {createNotice}
              </div>
            )}
            <Button
              variant="primary"
              size="lg"
              style={{ marginTop: 16, width: '100%' }}
              onClick={submitCreateType}
            >
              Create {createName.trim() || 'New type'}
            </Button>
          </div>
        </>
      )}

      {/* First-run guidance overlay (design D-Guide): a fixed full-screen layer above
          the sheet scrim and the create-type flow, gated to an in-match player's first
          Colony arrival. Its step is the store-held `guideStep` so it survives a route-
          exit unmount (cleared by `resetGuideProgress` above); dismiss persists once. */}
      {showGuide && (
        <FirstRunGuide
          step={guideStep}
          onNext={() => setGuideStep(guideStep + 1)}
          onBack={() => setGuideStep(guideStep - 1)}
          onDismiss={dismissGuide}
        />
      )}

      {/* **The pvp prototype's globally pinned prompt bar**: at the bottom of the *screen*, not
          inside the bee-type sheet, because the agent's unit of work is the whole colony — a
          player can say something while watching, without opening a type first. */}
      {pvpChat !== undefined && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: pvpPromptHeight,
            display: 'flex',
            background: 'var(--hud-bg)',
            borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
            boxShadow: 'var(--shadow-sheet)',
          }}
        >
          <PromptBar
            height={pvpPromptHeight}
            draft={promptDraft}
            focused={promptFocused}
            scopeName={selectedType?.name ?? null}
            canSend
            // Disabled for the whole of `isChatBusy` too (`beekeeper-progress-indicator` F2),
            // mirroring `SandboxScreen.tsx`'s own composer gate: `sendAndOpen` already refuses a
            // second send at the channel level, so this is a visible echo of a guard that already
            // exists, not a second one — `sendPvpPrompt` below deliberately does NOT re-check
            // busy-ness itself for the same reason.
            disabled={disabled || isChatBusy(pvpChatPhase)}
            disabledReason={
              disabled
                ? 'Spectating — prompts are disabled'
                : isChatBusy(pvpChatPhase)
                  ? 'The Beekeeper is already working on this.'
                  : ''
            }
            threadSummary={pvpThreadSummary}
            chatBusy={pvpChatPhase === 'submitting' || pvpChatPhase === 'running'}
            chatFailed={pvpChatPhase === 'failed'}
            chatUncertain={pvpChatPhase === 'uncertain'}
            chatNoOutputSettle={pvpChatSnapshot?.noOutputSettle}
            chatUnread={pvpChatSnapshot?.unread === true}
            chatMinimised={pvpChatSnapshot?.view === 'minimised'}
            onReopenThread={() => pvpChat.openExisting()}
            onDraft={setPromptDraft}
            onFocus={() => setPromptFocused(true)}
            onBlur={() => setPromptFocused(false)}
            onClearScope={() => selectColonyType(null)}
            onSend={sendPvpPrompt}
          />
        </div>
      )}

      {pvpChat !== undefined && pvpChatSnapshot?.view === 'open' && (
        <ChatOverlay
          transcript={pvpChatSnapshot.transcript.items}
          // The same phase-driven overlay the sandbox mounts
          // (`beekeeper-progress-indicator`): `submitting` shows progress from the press of Send
          // rather than from the server's first frame, and `uncertain` offers a refresh instead of
          // a resend it cannot vouch for.
          phase={pvpChatSnapshot.phase}
          awaitingFirstOutput={pvpChatSnapshot.awaitingFirstOutput}
          noOutputSettle={pvpChatSnapshot.noOutputSettle}
          statusLine={selectedType === null ? undefined : `Current selected type: ${selectedType.name}`}
          onSend={(text) => pvpChat.sendAndOpen(text, selectedType?.name ?? null)}
          onStop={() => pvpChat.stop()}
          // Minimise, not close (`player-chat-and-outcome` **D-2**) — the same wiring the sandbox
          // mount uses, so the one shared overlay behaves identically on both surfaces.
          onClose={() => pvpChat.minimise()}
          onRefresh={() => void pvpChat.rehydrate()}
          // "New chat" (WAG-94). Absent for the presentation's read-only preview, whose transport
          // cannot clear and therefore produces a seam without `clearConversation`.
          {...(pvpChat.clearConversation === undefined
            ? {}
            : { onClear: () => void pvpChat.clearConversation!() })}
        />
      )}
    </div>
  );
}

/** Advisory cooldown countdown (design D7): only meaningful once `accepted` — a
 * `Rejected{rateLimited}` (surfaced via `notReadyNotice`) is the authoritative
 * signal, this is a display hint derived from the accepted tick + the balance
 * cooldown seed.
 *
 * `tickRateHz` is **wire-required** (client-tick-rate-provenance E4): sourced from
 * `manifest.tickRateHz` by every caller — no defaulted argument. */
function promptCooldownSeconds(
  status: PromptStatus | undefined,
  nowTick: number,
  tickRateHz: number,
): number {
  if (status === undefined || status.status.kind !== 'accepted') return 0;
  const elapsed = Math.max(0, Math.floor((nowTick - status.tick) / tickRateHz));
  return Math.max(0, PROMPT_COOLDOWN_SECONDS - elapsed);
}

/** Player-wide advisory cooldown (design D-Starter, architecture-review F3): the
 * maximum UNEXPIRED advisory cooldown across ALL types' accepted statuses — the same
 * accepted-tick + balance-cooldown arithmetic `promptCooldownSeconds` uses, maxed over
 * every entry rather than only the selected type. This detects a type-A-accepted →
 * switch-to-idle-B cooldown before any send bounces (no wire-authoritative player-wide
 * cooldown exists — D-073), and returns 0 once every entry's cooldown has lapsed, so it
 * filters expired entries rather than latching off forever. */
function playerWideCooldownSeconds(
  byType: Record<number, PromptStatus>,
  nowTick: number,
  tickRateHz: number,
): number {
  let max = 0;
  for (const status of Object.values(byType)) {
    const remaining = promptCooldownSeconds(status, nowTick, tickRateHz);
    if (remaining > max) max = remaining;
  }
  return max;
}

// --- Version bar composition (design D6) ------------------------------------

interface VersionBarProps {
  colonyMeta: Parameters<typeof deriveVersionBarModel>[0]['colonyMeta'];
  versionCache: Record<string, string>;
  provenanceLog: Parameters<typeof deriveVersionBarModel>[0]['provenanceLog'];
  promptStatusByType: Parameters<typeof deriveVersionBarModel>[0]['promptStatusByType'];
  typeId: number;
  typeName: string;
  viewedVersion: number | null;
  setViewedVersion: (v: number | null) => void;
  disabled: boolean;
  nowTick: number;
  /** **wire-required** (client-tick-rate-provenance E4): sourced from `manifest.tickRateHz`;
   * `null` only in the brief pre-Welcome window, where the provenance summary below has no
   * real rate to divide by and falls back to its "no details yet" text instead of a guess. */
  tickRateHz: number | null;
  dispatch: (mutation: ScriptingMutation) => 'sent' | 'not-ready';
}

function VersionBar({
  colonyMeta,
  versionCache,
  provenanceLog,
  promptStatusByType,
  typeId,
  typeName,
  viewedVersion,
  setViewedVersion,
  disabled,
  nowTick,
  tickRateHz,
  dispatch,
}: VersionBarProps) {
  const model: VersionBarModel | null = deriveVersionBarModel(
    { colonyMeta, versionCache, provenanceLog, promptStatusByType },
    typeId,
  );
  if (model === null) return null;

  const showFailed = model.failedPip !== null && viewedVersion === null;
  if (showFailed) {
    const dismiss = () => setViewedVersion(model.liveVersion);
    return (
      <VersionRow
        style={{ marginTop: 8 }}
        version={null}
        failed
        summary="Last attempt failed"
        meta={model.failedPip!.diagnostics.join('; ')}
        canStepOlder
        canStepNewer
        onStepOlder={dismiss}
        onStepNewer={dismiss}
      />
    );
  }

  const viewedVersionNumber = viewedVersion ?? model.liveVersion;
  const idx = model.pips.findIndex((p) => p.version === viewedVersionNumber);
  const pip = model.pips[idx] ?? model.pips[model.pips.length - 1];
  if (pip === undefined) return null;

  const meta =
    pip.provenance && tickRateHz !== null
      ? formatProvenanceSummary(pip.provenance, nowTick, tickRateHz)
      : 'No details — activate to view';

  return (
    <VersionRow
      style={{ marginTop: 8 }}
      version={pip.version}
      summary={`Version ${pip.version}`}
      meta={meta}
      isLive={pip.isLive}
      canStepOlder={idx > 0}
      canStepNewer={idx < model.pips.length - 1}
      onStepOlder={() => setViewedVersion(model.pips[idx - 1]!.version)}
      onStepNewer={() => setViewedVersion(model.pips[idx + 1]!.version)}
      onActivate={
        !pip.isLive && !disabled
          ? () => dispatch({ type: 'rollback', value: { typeName, targetVersion: pip.version } })
          : undefined
      }
    />
  );
}

// --- Flowchart/Code tab body composition (design D4/D5) --------------------

interface ScriptBodyProps {
  colonyMeta: Parameters<typeof deriveVersionBarModel>[0]['colonyMeta'];
  versionCache: Record<string, string>;
  promptStatusByType: Record<number, { status: { kind: string } }>;
  typeId: number;
  typeName: string;
  typeColor: string;
  viewedVersion: number | null;
  activeTab: 'flowchart' | 'code';
  /** Spectator/eliminated gate (design D-Gate precedent) — HandEdit's Edit toggle is
   * offered only to an active player, mirroring every other mutation affordance. */
  disabled: boolean;
  /** The store's best-effort single most-recent-rejection slot (client-colony
   * "Mutation dispatch seam and rejection surfacing", D-073) — a `compileFailed`
   * observed while this HandEdit is awaiting its outcome reopens edit mode with the
   * diagnostics shown and the submitted draft preserved. */
  mutationRejection: RejectReason | null;
  dispatch: (mutation: ScriptingMutation) => 'sent' | 'not-ready';
}

function ScriptBody({
  colonyMeta,
  versionCache,
  typeId,
  typeName,
  typeColor,
  viewedVersion,
  activeTab,
  disabled,
  mutationRejection,
  dispatch,
}: ScriptBodyProps) {
  // HandEdit local state (client-colony "Read-only code view, editable via HandEdit for
  // an active player"): `ScriptBody` is remounted per selected type (the call site keys it
  // on `typeId`), so this state never leaks between types. `awaitingSave` is a one-shot
  // flag distinguishing "a compileFailed just now is about THIS type's just-submitted
  // HandEdit" from an unrelated stale rejection lingering in the shared single slot.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [awaitingSave, setAwaitingSave] = useState(false);
  const [notReadyNotice, setNotReadyNotice] = useState<string | null>(null);

  // Computed before the hooks below (a plain value, not itself a hook) so both effects can
  // depend on it — `typeMeta`'s lookup can miss (colonyMeta not loaded yet), in which case
  // `isLive` is vacuously false; every hook still runs on every render regardless (the
  // `typeMeta === undefined` early return sits after all hooks, never between them).
  const typeMeta = colonyMeta?.types.find((t) => t.typeId === typeId);
  const isLive =
    typeMeta !== undefined && (viewedVersion === null || viewedVersion === typeMeta.activeVersion);

  useEffect(() => {
    if (!awaitingSave || mutationRejection === null) return;
    // ANY refusal for the just-submitted HandEdit reopens edit mode with the draft preserved —
    // never silently discarding a player's edit (client-colony "Read-only code view, editable via
    // HandEdit for an active player"). A compileFailed (diagnostics shown, below) and a stale base
    // version (the type/document moved on since this draft's baseVersion — someone else's edit or
    // an accepted Beekeeper generation landed first) were the first two; rounds' own
    // `turnInProgress` (a running Beekeeper turn holds the sandbox's slot) extends the exact same
    // guarantee rather than needing a second reopening path, and so does every other rounds
    // hand-edit refusal (`unauthorized`, `invalid`) — only the diagnostics banner below is
    // `compileFailed`-specific.
    setEditing(true);
    setAwaitingSave(false);
  }, [mutationRejection, awaitingSave]);

  // Implementation-review finding (adopted): stepping the version bar away from the live
  // version while mid-edit must not leave the draft editor rendered over a now-non-live
  // (snapshot) slot — `CodeView`'s edit-mode branch renders on `editing` alone, so without
  // this the editor would keep showing (and Save would remain reachable) against a version
  // that is no longer editable. Closing edit mode here, rather than gating inside `CodeView`,
  // also correctly abandons any half-written draft and its `awaitingSave` wait.
  useEffect(() => {
    if (!isLive) {
      setEditing(false);
      setAwaitingSave(false);
    }
  }, [isLive]);

  if (typeMeta === undefined) return null;

  const sourceText = isLive
    ? typeMeta.canonicalText
    : versionCache[versionCacheKey(typeId, viewedVersion!)];

  if (sourceText === undefined) {
    return (
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 11,
            color: 'var(--text-muted)',
            background: 'var(--paper-1)',
            borderRadius: 10,
            padding: '7px 12px',
            marginBottom: 8,
          }}
        >
          Snapshot · read-only — source unavailable, activate to view
        </div>
        <div style={{ padding: '28px 12px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
          No cached copy from this session.
        </div>
      </div>
    );
  }

  if (activeTab === 'code') {
    const editable = isLive && !disabled;
    const diagnostics =
      mutationRejection?.kind === 'compileFailed' && editing ? mutationRejection.diagnostics : undefined;

    /** Dispatches a HandEdit for this type's program. The whole-document sibling this used to
     * carry — the rounds degraded view's repair surface — is the sandbox screen's type sheet now
     * (`sandbox-screen-consolidation` task 8.2), and with it the document-ordinal `baseVersion`
     * fork: a live edit's base is, and only ever was, the type's own active version. */
    function commitHandEdit(dispatchTypeName: string, source: string): void {
      setNotReadyNotice(null);
      const result = dispatch({
        type: 'handEdit',
        value: {
          typeName: dispatchTypeName,
          programSource: source,
          baseVersion: typeMeta!.activeVersion,
        },
      });
      if (result === 'not-ready') {
        setNotReadyNotice('Reconnecting — try again');
        return;
      }
      // Optimistic return to read-only (design.md Decisions): mirrors
      // Rollback/CreateType's own pattern — the eventual outcome (a new
      // ColonyMeta on success, or a mutationRejection on failure) reconciles
      // the view, not a synchronous result here.
      setAwaitingSave(true);
      setEditing(false);
    }

    return (
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8, opacity: isLive ? 1 : 0.5 }}>
        {notReadyNotice && (
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: 'var(--danger)',
              background: 'var(--danger-soft)',
              borderRadius: 'var(--r-md)',
              padding: '7px 10px',
              marginBottom: 8,
            }}
          >
            {notReadyNotice}
          </div>
        )}
        <CodeView
          code={sourceText}
          editable={editable}
          editing={editing}
          onToggleEdit={() => {
            setDraft(sourceText);
            setNotReadyNotice(null);
            setEditing(true);
          }}
          draft={draft}
          onDraftChange={setDraft}
          diagnostics={diagnostics}
          onCancel={() => {
            setEditing(false);
            setNotReadyNotice(null);
          }}
          onSave={() => commitHandEdit(typeName, draft)}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        paddingBottom: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <BppProgramDiagram source={sourceText} label="B++ program diagram" color={typeColor} />
    </div>
  );
}
